import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Timer, ArrowLeft, Star, Target } from 'lucide-react';
import { cn } from '../lib/utils';
import HowToPlay from './HowToPlay';
import FeverOverlay from './FeverOverlay';
import GameModal from './GameModal';

import { audioEngine } from '../utils/audioEngine';
import { PrismBuffs } from '../utils/prismSystem';

interface TileData {
  id: string;
  value: number;
}

interface ShellCrushGameProps {
  onGameOver: (stats: { 
    score: number; 
    correctAnswers: number; 
    mistakes: number; 
    feverAnswers: number;
    feverTriggerCount: number;
    maxBlitzStreak: number;
  }) => void;
  onBack: () => void;
  onUpdateMission?: (id: string, amount?: number) => void;
  prismBuffs: PrismBuffs;
  highScore: number;
  tutorialStep?: number;
  onNextTutorialStep?: () => void;
}

const ROWS = 8;
const COLS = 8;
const TILE_VALUES = [1, 2, 3, 4, 5, 6, 9];
const TILE_BONUS = 50;
const FEVER_TILE_BONUS = 50;
const INITIAL_TIME = 60;

const ShellTile = ({ value, isSelected, isBlue, isFever }: { value: number, isSelected?: boolean, isBlue?: boolean, isFever?: boolean }) => {
  return (
    <div className={cn(
      "w-full h-full relative flex items-center justify-center transition-all duration-300",
      isSelected && "scale-110"
    )}>
      {/* Shell Shape (SVG Path) */}
      <svg viewBox="0 0 100 100" className={cn(
        "absolute inset-0 w-full h-full drop-shadow-[0_4px_4px_rgba(0,0,0,0.1)]",
        isSelected ? "drop-shadow-[0_0_12px_rgba(251,191,36,0.6)]" : ""
      )}>
        <defs>
          <linearGradient id="whiteShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#f1f5f9" />
          </linearGradient>
          <linearGradient id="blueShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#0ea5e9" />
          </linearGradient>
          <linearGradient id="feverShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f43f5e" />
            <stop offset="100%" stopColor="#881337" />
          </linearGradient>
          <linearGradient id="selectedShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
        <path 
          d="M50 5 L85 25 L95 60 L75 90 L50 95 L25 90 L5 60 L15 25 Z" 
          fill={isSelected ? "url(#selectedShellGrad)" : (isFever ? "url(#feverShellGrad)" : (isBlue ? "url(#blueShellGrad)" : "url(#whiteShellGrad)"))}
          className="transition-colors duration-300"
          stroke={isSelected ? "#fbbf24" : "#e2e8f0"}
          strokeWidth="2"
        />
        {/* Ridge details */}
        <path d="M50 5 L50 95" stroke="black" strokeWidth="1" strokeOpacity="0.05" fill="none" />
        <path d="M25 15 L35 92" stroke="black" strokeWidth="1" strokeOpacity="0.03" fill="none" />
        <path d="M75 15 L65 92" stroke="black" strokeWidth="1" strokeOpacity="0.03" fill="none" />
      </svg>
      {/* Value Overlay */}
      <span className={cn(
        "relative z-10 font-num text-xl font-black",
        isSelected || isFever || isBlue ? "text-white" : "text-slate-700"
      )}>
        {value}
      </span>
    </div>
  );
};

export default function ShellCrushGame({ onGameOver, onBack, onUpdateMission, prismBuffs, highScore, tutorialStep, onNextTutorialStep }: ShellCrushGameProps) {
  const isTutorialActive = tutorialStep !== undefined && tutorialStep >= 7 && tutorialStep <= 13;
  const [grid, setGrid] = useState<TileData[][]>([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME + (prismBuffs?.extraTime || 0));
  const [selectedPath, setSelectedPath] = useState<{ x: number; y: number }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [startSequence, setStartSequence] = useState<'ready' | 'go' | null>(() => isTutorialActive ? null : 'ready');
  const [shatteringTiles, setShatteringTiles] = useState<{ x: number; y: number; value: number; id: string }[]>([]);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [feverGauge, setFeverGauge] = useState(0);
  const [isFever, setIsFever] = useState(false);
  const [feverTimeLeft, setFeverTimeLeft] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [feverAnswers, setFeverAnswers] = useState(0);
  const [feverTriggerCount, setFeverTriggerCount] = useState(0);
  const [blitzStreak, setBlitzStreak] = useState(0);
  const [maxBlitzStreak, setMaxBlitzStreak] = useState(0);
  const [lastMatchTime, setLastMatchTime] = useState(Date.now());
  
  const [hintPath, setHintPath] = useState<{x: number, y: number}[] | null>(null);
  const lastInteractionTimeRef = useRef(Date.now());
  const lastInitStepRef = useRef<number | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  // Initialize Grid
  const initGrid = useCallback(() => {
    const isTutorial = isTutorialActive;
    const newGrid = [];

    if (isTutorial) {
      const presetValues = [
        [6, 2, 4, 9, 9, 5, 1, 3],
        [4, 3, 3, 4, 2, 1, 6, 3],
        [4, 9, 3, 4, 3, 4, 1, 4],
        [6, 3, 5, 2, 5, 4, 4, 1],
        [1, 2, 9, 6, 1, 9, 2, 2],
        [4, 2, 3, 5, 9, 2, 2, 6],
        [3, 2, 2, 2, 2, 5, 1, 4],
        [3, 4, 3, 9, 3, 1, 5, 5]
      ];

      for (let y = 0; y < ROWS; y++) {
        const row = [];
        for (let x = 0; x < COLS; x++) {
          row.push({
            id: `${y}-${x}-${Math.random().toString(36).substr(2, 5)}`,
            value: presetValues[y][x]
          });
        }
        newGrid.push(row);
      }
    } else {
      for (let y = 0; y < ROWS; y++) {
        const row = [];
        for (let x = 0; x < COLS; x++) {
          row.push({
            id: Math.random().toString(36).substr(2, 9),
            value: TILE_VALUES[Math.floor(Math.random() * TILE_VALUES.length)]
          });
        }
        newGrid.push(row);
      }
    }

    setGrid(newGrid);
  }, [tutorialStep]);

  useEffect(() => {
    if ((tutorialStep === 11 || tutorialStep === 12 || tutorialStep === 13) && !isFever) {
      setIsFever(true);
      setFeverTimeLeft(15);
      setFeverGauge(10);
      audioEngine.playFeverStart('shellcrush');
    }
  }, [tutorialStep, isFever]);

  // Only initialize the board once on mount/load
  useEffect(() => {
    if (tutorialStep !== undefined && tutorialStep >= 7 && tutorialStep <= 13) {
      if (lastInitStepRef.current !== tutorialStep) {
        const hasAlreadyInitializedTutorial = lastInitStepRef.current !== null && lastInitStepRef.current >= 7 && lastInitStepRef.current <= 13;
        if (!hasAlreadyInitializedTutorial) {
          lastInitStepRef.current = tutorialStep;
          initGrid();
        }
      }
    } else {
      if (lastInitStepRef.current === null) {
        lastInitStepRef.current = -1;
        initGrid();
      }
    }
  }, [tutorialStep, initGrid]);

  // Guarantee tutorial targets are present when moving to steps 11, 12, and 13
  useEffect(() => {
    if (!tutorialStep || tutorialStep < 10) return;
    setGrid(prevGrid => {
      if (prevGrid.length === 0) return prevGrid;
      const nextGrid = prevGrid.map(row => row.map(tile => ({ ...tile })));
      if (tutorialStep === 11 || tutorialStep === 12 || tutorialStep === 13) {
        nextGrid[4][4].value = 1;
        nextGrid[4][5].value = 9;
      }
      return nextGrid;
    });
  }, [tutorialStep]);

  // Start background music when gameplay starts (after countdown)
  useEffect(() => {
    if (showHowTo || startSequence !== null) return;
    audioEngine.startMusic(isFever ? 'shellcrush_fever' : 'shellcrush');
    // Music routing handled by App.tsx
  }, [showHowTo, startSequence, isFever]);

  // Start Sequence
  useEffect(() => {
    if (showHowTo) return;
    if (startSequence === 'ready') {
      audioEngine.playSound('ready');
      const t = setTimeout(() => setStartSequence('go'), 1000);
      return () => clearTimeout(t);
    } else if (startSequence === 'go') {
      audioEngine.playSound('go');
      const t = setTimeout(() => setStartSequence(null), 1000);
      return () => clearTimeout(t);
    }
  }, [startSequence, showHowTo]);

  const handleGameOver = useCallback(() => {
    onGameOver({
      score,
      correctAnswers,
      mistakes,
      feverAnswers,
      feverTriggerCount,
      maxBlitzStreak
    });
  }, [score, correctAnswers, mistakes, feverAnswers, feverTriggerCount, maxBlitzStreak, onGameOver]);

  useEffect(() => {
    if (timeLeft === 0 && !isFever) {
      handleGameOver();
    }
  }, [timeLeft, isFever, handleGameOver]);

  // Timer
  useEffect(() => {
    if (isFever || startSequence || showHowTo || timeLeft === 0 || isTutorialActive) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [isFever, startSequence, showHowTo, timeLeft, isTutorialActive]);

  // Fever Timer
  useEffect(() => {
    if (!isFever || startSequence || showHowTo || isTutorialActive) return;

    const interval = setInterval(() => {
      setFeverTimeLeft((prev) => {
        if (prev <= 1) {
          setIsFever(false);
          setFeverGauge(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isFever, startSequence, showHowTo]);

  const isAdjacent = (p1: { x: number; y: number }, p2: { x: number; y: number }) => {
    const dx = Math.abs(p1.x - p2.x);
    const dy = Math.abs(p1.y - p2.y);
    return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
  };

  const handlePointerDown = (x: number, y: number) => {
    if (startSequence || showHowTo || timeLeft === 0) return;
    
    lastInteractionTimeRef.current = Date.now();
    setHintPath(null);

    setIsDragging(true);
    setSelectedPath([{ x, y }]);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || startSequence || showHowTo) return;

    const element = document.elementFromPoint(e.clientX, e.clientY);
    if (!element) return;

    const tileElement = element.closest('[data-tile]');
    if (!tileElement) return;

    const x = parseInt(tileElement.getAttribute('data-x') || '-1');
    const y = parseInt(tileElement.getAttribute('data-y') || '-1');

    if (x === -1 || y === -1) return;

    const last = selectedPath[selectedPath.length - 1];
    if (last.x === x && last.y === y) return;

    const index = selectedPath.findIndex(p => p.x === x && p.y === y);
    if (index !== -1) {
      setSelectedPath(selectedPath.slice(0, index + 1));
      return;
    }

    if (isAdjacent(last, { x, y })) {
      setSelectedPath([...selectedPath, { x, y }]);
    }
  };

  const findHint = useCallback((currentGrid: TileData[][]) => {
    const dfs = (x: number, y: number, currentPath: {x: number, y: number}[], currentSum: number): {x: number, y: number}[] | null => {
      if (x < 0 || x >= COLS || y < 0 || y >= ROWS) return null;
      if (currentPath.some(p => p.x === x && p.y === y)) return null;
      
      const val = currentGrid[y][x].value;
      if (val === 0) return null;
      
      const newSum = currentSum + val;
      const newPath = [...currentPath, {x, y}];
      
      if (newSum === 10) return newPath;
      if (newSum > 10) return null;
      
      const dirs = [{dx: 1, dy: 0}, {dx: -1, dy: 0}, {dx: 0, dy: 1}, {dx: 0, dy: -1}];
      for (const d of dirs) {
        const res = dfs(x + d.dx, y + d.dy, newPath, newSum);
        if (res) return res;
      }
      return null;
    };

    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        const hint = dfs(x, y, [], 0);
        if (hint) return hint;
      }
    }
    return null;
  }, []);

  // Inactivity Hint Timer
  useEffect(() => {
    if (isFever || startSequence || showHowTo || isDragging || timeLeft === 0 || hintPath !== null || isTutorialActive) return;
    
    const interval = setInterval(() => {
      if (Date.now() - lastInteractionTimeRef.current >= 5000) {
        const hint = findHint(grid);
        if (hint) {
          setHintPath(hint);
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [grid, isFever, startSequence, showHowTo, isDragging, timeLeft, hintPath, findHint]);

  const applyGravity = (currentGrid: TileData[][]) => {
    const newGrid = [...currentGrid.map(row => [...row])];
    for (let x = 0; x < COLS; x++) {
      const column = [];
      for (let y = ROWS - 1; y >= 0; y--) {
        if (newGrid[y][x].value !== 0) {
          column.push(newGrid[y][x]);
        }
      }
      for (let y = ROWS - 1; y >= 0; y--) {
        if (column.length > 0) {
          newGrid[y][x] = column.shift()!;
        } else {
          newGrid[y][x] = {
            id: Math.random().toString(36).substr(2, 9),
            value: TILE_VALUES[Math.floor(Math.random() * TILE_VALUES.length)]
          };
        }
      }
    }
    return newGrid;
  };

  const handlePointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const uniqueNineClusters = new Set<string>();
    let currentSum = 0;
    
    selectedPath.forEach(p => {
      const tile = grid[p.y][p.x];
      if (tile.value === 9) {
        let ClusterIdFound = false;
        const neighbors = [
          { x: p.x + 1, y: p.y },
          { x: p.x - 1, y: p.y },
          { x: p.x, y: p.y + 1 },
          { x: p.x, y: p.y - 1 },
        ];
        
        for (const n of neighbors) {
          if (uniqueNineClusters.has(`${n.x},${n.y}`)) {
            ClusterIdFound = true;
            break;
          }
        }

        if (!ClusterIdFound) {
          currentSum += 9;
        }
        uniqueNineClusters.add(`${p.x},${p.y}`);
      } else {
        currentSum += tile.value;
      }
    });

    const hasNine = selectedPath.some(p => grid[p.y][p.x].value === 9);

    if (currentSum > 0 && currentSum % 10 === 0) {
      // Tutorial Strict Path Enforcement
      if (tutorialStep === 8) {
        if (selectedPath.length !== 3 || currentSum !== 10) {
          audioEngine.playWrong();
          setSelectedPath([]);
          setIsDragging(false);
          return;
        }
        audioEngine.playCorrect(feverGauge, 'shellcrush', hasNine, isFever);
        setScore(prev => prev + 30);
        onNextTutorialStep?.();
      } else if (tutorialStep === 10) {
        if (selectedPath.length !== 2 || currentSum !== 10) {
          audioEngine.playWrong();
          setSelectedPath([]);
          setIsDragging(false);
          return;
        }
        audioEngine.playCorrect(feverGauge, 'shellcrush', hasNine, isFever);
        setScore(prev => prev + 50);
        onNextTutorialStep?.();
      } else if (tutorialStep === 12) {
        if (selectedPath.length !== 2 || currentSum !== 10) {
          audioEngine.playWrong();
          setSelectedPath([]);
          setIsDragging(false);
          return;
        }
        audioEngine.playCorrect(feverGauge, 'shellcrush', hasNine, isFever);
        setScore(prev => prev + 180);
        onNextTutorialStep?.();
      }

      if (!tutorialStep || tutorialStep < 8 || tutorialStep > 12) {
        audioEngine.playCorrect(feverGauge, 'shellcrush', hasNine, isFever);
      }

      if (tutorialStep && tutorialStep >= 8 && tutorialStep <= 12) {
         // During tutorial, we handle scoring manually above
      } 

      const toDestroy = [...selectedPath];
      const visited = new Set(selectedPath.map(p => `${p.x},${p.y}`));
      const queue = [...selectedPath];

      // Cascade logic for '9'
      while (queue.length > 0) {
        const curr = queue.shift()!;
        if (grid[curr.y][curr.x].value === 9) {
          const neighbors = isFever ? [
            { x: curr.x + 1, y: curr.y },
            { x: curr.x - 1, y: curr.y },
            { x: curr.x, y: curr.y + 1 },
            { x: curr.x, y: curr.y - 1 },
            { x: curr.x + 1, y: curr.y + 1 },
            { x: curr.x - 1, y: curr.y - 1 },
            { x: curr.x + 1, y: curr.y - 1 },
            { x: curr.x - 1, y: curr.y + 1 },
          ] : [
            { x: curr.x + 1, y: curr.y },
            { x: curr.x - 1, y: curr.y },
            { x: curr.x, y: curr.y + 1 },
            { x: curr.x, y: curr.y - 1 },
          ];
          neighbors.forEach(n => {
            const key = `${n.x},${n.y}`;
            if (n.x >= 0 && n.x < COLS && n.y >= 0 && n.y < ROWS && !visited.has(key)) {
              visited.add(key);
              toDestroy.push(n);
              queue.push(n);
            }
          });
        }
      }

      // Shattering effect
      const shatterData = toDestroy.map(p => ({
        x: p.x,
        y: p.y,
        value: grid[p.y][p.x].value,
        id: Math.random().toString(36).substr(2, 9)
      }));
      setShatteringTiles(prev => [...prev, ...shatterData]);
      setTimeout(() => {
        setShatteringTiles(prev => prev.filter(t => !shatterData.some(sd => sd.id === t.id)));
      }, 1000);

      // 1. Calculate Core Path Points
      const corePathPoints = selectedPath.length * (isFever ? FEVER_TILE_BONUS : TILE_BONUS);

      // 2. Scan for 9-tiles in the selected path and find adjacent blast tiles
      const selectedSet = new Set(selectedPath.map(p => `${p.x},${p.y}`));
      const uniqueExplodedAdjacent = new Set<string>();

      selectedPath.forEach(p => {
        if (grid[p.y][p.x].value === 9) {
          const neighbors = isFever ? [
            { x: p.x + 1, y: p.y },
            { x: p.x - 1, y: p.y },
            { x: p.x, y: p.y + 1 },
            { x: p.x, y: p.y - 1 },
            { x: p.x + 1, y: p.y + 1 },
            { x: p.x - 1, y: p.y - 1 },
            { x: p.x + 1, y: p.y - 1 },
            { x: p.x - 1, y: p.y + 1 },
          ] : [
            { x: p.x + 1, y: p.y },
            { x: p.x - 1, y: p.y },
            { x: p.x, y: p.y + 1 },
            { x: p.x, y: p.y - 1 },
          ];

          neighbors.forEach(n => {
            if (n.x >= 0 && n.x < COLS && n.y >= 0 && n.y < ROWS) {
              const key = `${n.x},${n.y}`;
              if (!selectedSet.has(key)) {
                uniqueExplodedAdjacent.add(key);
              }
            }
          });
        }
      });

      const explosionPoints = uniqueExplodedAdjacent.size * (isFever ? FEVER_TILE_BONUS : TILE_BONUS);
      let points = corePathPoints + explosionPoints;
      if (isFever) {
        points = Math.round(points * (1 + (prismBuffs?.feverScoreMultiplier || 0)));
      }
      
      if (onUpdateMission) {
        onUpdateMission('math_god', toDestroy.length);
      }

      setScore(prev => prev + points);
      setCorrectAnswers(prev => prev + 1);
      if (isFever) {
        setFeverAnswers(prev => prev + 1);
      }

      if (!isFever) {
        const newGauge = Math.min(10, feverGauge + 1);
        setFeverGauge(newGauge);
        if (newGauge >= 10) {
          setIsFever(true);
          audioEngine.playFeverStart('shellcrush');
          setFeverTriggerCount(prev => prev + 1);
          setFeverTimeLeft(15 + (prismBuffs?.extraFever || 0));
          setFeverGauge(10);
        }
      }

      // Streak tracking
      const now = Date.now();
      const timeTaken = (now - lastMatchTime) / 1000;
      setLastMatchTime(now);

      const BLITZ_THRESHOLD = 1.5;
      if (timeTaken <= BLITZ_THRESHOLD) {
        const newBlitzStreak = blitzStreak + 1;
        setBlitzStreak(newBlitzStreak);
        setMaxBlitzStreak(prev => Math.max(prev, newBlitzStreak));
      } else {
        setBlitzStreak(0);
      }

      // Update Grid
      const nextGrid = [...grid.map(row => [...row])];
      toDestroy.forEach(p => {
        nextGrid[p.y][p.x] = { ...nextGrid[p.y][p.x], value: 0 };
      });

      setGrid(applyGravity(nextGrid));
    } else if (selectedPath.length > 0) {
      audioEngine.playWrong();
      setMistakes(prev => prev + 1);
      setBlitzStreak(0);
    }

    setSelectedPath([]);
    lastInteractionTimeRef.current = Date.now();
  }, [isDragging, selectedPath, grid, isFever, blitzStreak, feverGauge, lastMatchTime, onUpdateMission, prismBuffs]);

  useEffect(() => {
    window.addEventListener('pointerup', handlePointerUp);
    return () => window.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerUp]);

  return (
    <div 
      className={cn(
        "flex flex-col h-full transition-all duration-700 p-6 select-none relative overflow-hidden touch-none",
        isFever ? "bg-gradient-to-b from-[#f43f5e] to-[#f97316]" : "bg-gradient-to-b from-[#0ea5e9] to-[#0284c7]"
      )}
    >
      {/* Background Pattern - Water Caustics / Ripple effect */}
      <div className="absolute inset-0 opacity-30 pointer-events-none mix-blend-overlay" style={{ 
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.8) 0%, transparent 60%), radial-gradient(circle at 20% 80%, rgba(255,255,255,0.8) 0%, transparent 50%)',
          backgroundSize: '100px 100px',
          backgroundPosition: '0 0, 50px 50px'
      }} />
      <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
      
      <FeverOverlay isActive={isFever} />

      {/* Header Slab */}
      <div className="bg-white shadow-[0_10px_30px_rgba(2,132,199,0.2)] rounded-[32px] p-6 mb-4 border-b-8 border-slate-200 relative z-10 overflow-hidden ring-4 ring-white/50">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '6px 6px' }} />
        
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => {
              if (isTutorialActive) {
                onBack();
              } else {
                setShowExitConfirm(true);
              }
            }} 
            className="p-2 bg-[#fef3c7] text-[#d97706] rounded-xl border-b-[3px] border-[#f59e0b] shadow-sm active:translate-y-[3px] active:border-b-0 transition-all relative z-10"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          </button>
          
          <div className="flex flex-col items-center">
            <div className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Best: {highScore.toLocaleString()}</div>
            <motion.span 
              key={score}
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-4xl font-num text-[#0ea5e9] drop-shadow-sm relative z-10 leading-none"
            >
              {score.toLocaleString()}
            </motion.span>
          </div>
          
          <div className="w-10" />
        </div>

        {/* Timer Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-end mb-2">
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 shadow-inner relative z-10">
              <Timer className={cn("w-4 h-4 transition-colors duration-500", timeLeft <= 10 ? "text-rose-500 animate-pulse" : (timeLeft <= 30 ? "text-yellow-500" : "text-emerald-500"))} />
              <span className={cn("text-xl font-num tabular-nums font-black transition-colors duration-500", timeLeft <= 10 ? "text-rose-500" : "text-slate-700")}>
                {Math.ceil(isFever ? feverTimeLeft : timeLeft)}s
              </span>
            </div>
          </div>
          <div className="h-4 bg-slate-100 rounded-full overflow-hidden border-none shadow-inner p-0.5 relative z-10">
            <motion.div
              className={cn("h-full rounded-full transition-colors duration-500", isFever ? "bg-purple-500" : (timeLeft <= 10 ? "bg-rose-500" : (timeLeft <= 30 ? "bg-yellow-400" : "bg-emerald-400")))}
              initial={{ width: '100%' }}
              animate={{ width: isFever ? `${(feverTimeLeft / (15 + (prismBuffs?.extraFever || 0))) * 100}%` : `${(timeLeft / (INITIAL_TIME + (prismBuffs?.extraTime || 0))) * 100}%` }}
              transition={{ type: 'tween', ease: 'linear', duration: 1 }}
            />
          </div>
        </div>

        {/* Fever Energy */}
        <div className="relative z-10">
          <div className="flex justify-between text-[8px] font-black mb-2 uppercase tracking-[0.2em] opacity-60">
            <span>{isFever ? "FEVER STATUS" : "FEVER ENERGY"}</span>
            <span>{isFever ? "ACTIVE" : `${feverGauge}/10`}</span>
          </div>
          <div className="flex gap-1 h-5 p-1 rounded-xl bg-slate-100 shadow-inner">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "flex-1 rounded-sm transition-all duration-300",
                  (isFever || i < feverGauge) ? "bg-[#db2777]" : "bg-white"
                )}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Grid Area - Sand Island */}
      <div className="flex-1 flex items-center justify-center relative z-20" ref={gridRef}>
        <div 
          className="grid gap-1.5 p-4 bg-[#fef3c7] shadow-[0_30px_60px_rgba(2,132,199,0.5),inset_0_0_20px_rgba(255,255,255,0.8)] rounded-[40px] border-b-[12px] border-[#f59e0b] relative overflow-hidden ring-4 ring-white/20"
          style={{ 
            gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
          }}
          onPointerLeave={handlePointerUp}
          onPointerDown={() => {
            if (startSequence || showHowTo) return;
            setIsDragging(true);
          }}
          onPointerMove={handlePointerMove}
        >
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-multiply" style={{ backgroundImage: 'radial-gradient(#92400e 1px, transparent 1px)', backgroundSize: '6px 6px' }} />
          
          {grid.map((row, y) => (
            row.map((tile, x) => {
              const isSelected = selectedPath.some(p => p.x === x && p.y === y);
              const isHinted = hintPath?.some(p => p.x === x && p.y === y);

              return (
                <motion.div
                  key={tile.id}
                  id={`shell-tile-${x}-${y}`}
                  data-tile
                  data-x={x}
                  data-y={y}
                  onPointerDown={() => handlePointerDown(x, y)}
                  layout
                  initial={{ y: -50, opacity: 0 }}
                  animate={{ 
                    y: 0, 
                    opacity: tile.value === 0 ? 0 : 1,
                    scale: isSelected ? 1.15 : 1,
                    rotate: isHinted ? [-5, 5, -5, 5, 0] : 0
                  }}
                  transition={{ 
                    rotate: isHinted ? { repeat: Infinity, duration: 0.5 } : undefined,
                    layout: { type: "spring", stiffness: 300, damping: 25 },
                  }}
                  className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center cursor-pointer relative"
                >
                  {tile.value !== 0 && (
                    <ShellTile 
                      value={tile.value} 
                      isSelected={isSelected} 
                      isBlue={tile.value === 9} 
                      isFever={isFever && tile.value === 9}
                    />
                  )}
                  {isHinted && (
                    <div className="absolute inset-0 rounded-full border-4 border-yellow-400 animate-ping pointer-events-none" />
                  )}
                </motion.div>
              );
            })
          ))}

          {/* Shattering Animation */}
          <AnimatePresence>
            {shatteringTiles.map((tile) => (
              <motion.div
                key={tile.id}
                initial={{ 
                  x: 8 + tile.x * 45, 
                  y: 8 + tile.y * 45,
                  scale: 1,
                  opacity: 1
                }}
                animate={{ 
                  x: 8 + tile.x * 45 + (Math.random() - 0.5) * 300,
                  y: 8 + tile.y * 45 + (Math.random() - 0.5) * 300,
                  scale: 0,
                  opacity: 0,
                  rotate: 720
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="absolute w-10 h-10 z-[50] pointer-events-none"
              >
                <ShellTile value={tile.value} isSelected={true} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-6 text-center">
        <p className="text-[10px] font-black text-white/60 uppercase tracking-[0.2em] drop-shadow-md">
          CONNECT TILES TO REACH <span className="text-yellow-400">MULTIPLES OF 10</span>
        </p>
      </div>

      {/* Instructions Overlay */}
      <AnimatePresence>
        {showHowTo && (
          <HowToPlay mode="sum10" onClose={() => setShowHowTo(false)} />
        )}
      </AnimatePresence>

      {/* Start Sequence */}
      <AnimatePresence>
        {startSequence && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm"
          >
            <motion.div
              key={startSequence}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 2, opacity: 0 }}
              className="text-center"
            >
              <div className="text-7xl font-num text-yellow-400 italic font-black drop-shadow-2xl">
                {startSequence === 'ready' ? 'READY?' : 'GO!'}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <GameModal
        isOpen={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        onConfirm={onBack}
        type="confirm"
        title="QUIT GAME?"
        message="Your progress will be lost. Are you sure?"
        confirmLabel="QUIT"
        cancelLabel="STAY"
      />
    </div>
  );
}
